import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { MenuItem } from 'primeng/api';
import { StorageKeys, StoreService } from '../../services/store.service';
import { Store, StoreDef } from '@ngneat/elf';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  template: `
      <p-toolbar>
        <ng-template #start>
        <p-tabs [value]="activeTabIndex">
          @if (auth.isAuthenticated$ | async) {
            <p-tablist>
              @for (item of items; track $index) {
                <p-tab 
                  [value]="$index"
                  (click)="navigateToTab(item.label)">
                  {{ item.label }}
                </p-tab>
              }
            </p-tablist>
          }
        </p-tabs>
        </ng-template>

        <ng-template #center>
          @if (auth.isAuthenticated$ | async) {
            <div class="flex justify-center items-center">
              <span class="text-[#5C6BC0] font-medium" style="letter-spacing: 0.1em">PERI</span><span class="text-red-500 font-medium" style="letter-spacing: 0.1em">FOR</span>
            </div>
          }
        </ng-template>

        <ng-template #end>
          <div class="space-x-4 pt-1 pr-2">
            <p-button [rounded]="true" [icon]="isDarkMode ? 'pi pi-sun' : 'pi pi-moon'"  [text]="true" [title]="isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'" (onClick)="toggleDarkMode()"/>
            @if (auth.isAuthenticated$ | async) {
              <app-notification-hub></app-notification-hub>
            }
            @if (auth.isAuthenticated$ | async) {
              <app-help-menu/>
            }
            @if (auth.isAuthenticated$ | async) {
              <app-user-menu/>
            }
          </div>
        </ng-template>
      </p-toolbar>
    `,
  standalone: false
})
export class HeaderComponent implements OnInit {
  public items: MenuItem[] = [
    {
      label: 'Designs'
    },
    {
      label: 'Reports'
    }
    // Add other module levels here, ie: Reports, Etc
  ];

  public isDarkMode;
  public activeTabIndex = 0;
  private currentDesignId: string | null = null;

  constructor(
    public auth: AuthService,
    @Inject(DOCUMENT) public document,
    private _storeService: StoreService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) { }

  public themeStore: Store<StoreDef<{ isDarkMode: boolean }>>;

  ngOnInit() {
    this.themeStore = this._storeService.createStore(StorageKeys.DARK_MODE, {});

    this.themeStore.subscribe((v) => {
      this.isDarkMode = v?.isDarkMode ?? true;
      if (this.isDarkMode) {
        document.querySelector('html')?.classList.add('perifor-app-dark');
      } else {
        document.querySelector('html')?.classList.remove('perifor-app-dark');
      }
    });

    // Track the current designId from route params
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateDesignId();
    });
    
    // Initial load
    this.updateDesignId();
  }

  private updateDesignId(): void {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.currentDesignId = route.snapshot.paramMap.get('designId');

    if (!this.currentDesignId) {
      this.items = this.items.filter(item => item.label !== 'Reports' && item.label !== 'Integrations');
    } else {
      if (!this.items.find(item => item.label === 'Reports')) {
        this.items.push({ label: 'Reports' });
      }
      if (!this.items.find(item => item.label === 'Integrations')) {
        this.items.push({ label: 'Integrations' });
      }
    }

    // Update active tab based on current route
    const url = this.router.url;
    if (url.includes('/reports')) {
      this.activeTabIndex = 1;
    } else if (url.includes('/integrations')) {
      this.activeTabIndex = 2;
    } else {
      this.activeTabIndex = 0;
    }
  }

  navigateToTab(label: string) {
    if (label === 'Designs') {
      this.activeTabIndex = 0;
      this.router.navigate(['/designs', this.currentDesignId || '']);
    } else if (label === 'Reports' && this.currentDesignId) {
      this.activeTabIndex = 1;
      this.router.navigate(['/designs', this.currentDesignId, 'reports']);
    } else if (label === 'Integrations' && this.currentDesignId) {
      this.activeTabIndex = 2;
      this.router.navigate(['/designs', this.currentDesignId, 'integrations']);
    }
  }

  toggleDarkMode(): void {
    // Get current state and toggle it
    const currentState = this.themeStore.getValue();
    this.isDarkMode = !currentState.isDarkMode;

    // Update the store with the new value
    this._storeService.updateStore(StorageKeys.DARK_MODE, { isDarkMode: this.isDarkMode });

    // Toggle the dark mode class on the HTML element
    document.querySelector('html')?.classList.toggle('perifor-app-dark', this.isDarkMode);
  }
}
